/**
 * Light/dark theme toggle. Tokens are defined in tokens.css with a
 * `[data-theme="dark"]` block, so flipping the attribute on
 * documentElement swaps every color in the design system.
 *
 * Preference is stored in localStorage and survives reloads. If the user
 * hasn't picked one, we fall back to the OS preference.
 */
import { useEffect, useState } from 'react';

export type Theme = 'light' | 'dark';
const STORAGE_KEY = 'tr-theme';

function readStoredTheme(): Theme | null {
  if (typeof window === 'undefined') return null;
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' ? v : null;
}

function readSystemTheme(): Theme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function initialTheme(): Theme {
  return readStoredTheme() ?? readSystemTheme();
}

export function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
}

export function useTheme(): [Theme, (next: Theme) => void, () => void] {
  const [theme, setThemeState] = useState<Theme>(() => initialTheme());

  useEffect(() => {
    applyTheme(theme);
    window.localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  function setTheme(next: Theme) {
    setThemeState(next);
  }
  function toggle() {
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }

  return [theme, setTheme, toggle];
}
